import * as React from "react";
import Bugsnag from "@bugsnag/js";
import * as BugsnagPluginReact from "@bugsnag/plugin-react";
import { getTraceId } from "utils/get-trace-id";

let Boundary: React.ComponentType<{ children?: React.ReactNode }> =
  React.Fragment;

// wrap app with bugsnag error boundary
function BugsnagBoundary({ children }: { children: JSX.Element }) {
  return <Boundary>{children}</Boundary>;
}

function initialiseBugsnag() {
  if (import.meta.env.VITE_BUGSNAG_API_KEY) {
    Bugsnag.start({
      apiKey: import.meta.env.VITE_BUGSNAG_API_KEY,
      plugins: [new BugsnagPluginReact.default()],
      redactedKeys: [
        /^password$/i,
        /^token$/i,
        /^email$/i,
        /^given_name$/i,
        /^family_name$/i,
        /^remote_id$/i,
      ], //regex of the w
      onError: async (event) => {
        const error = event.originalError;
        if (error.name === "FetchClientError") return false;

        const errorMessage = error?.message;

        if ("request" in error && "response" in error) {
          const requestUrl = (error.request as Request)?.url;
          try {
            const request = await (error.request as Request)?.clone().json();
            const response = await (error.response as Response)?.clone().json();
            event.addMetadata("API Request", {
              requestUrl,
              request: request,
              message: errorMessage,
              response: response,
            });
          } catch {
            event.addMetadata("API Request", {
              requestUrl,
              message: errorMessage,
              request: "failed to parse request",
              response: "failed to parse response",
            });
          }
        } else {
          event.addMetadata("API Request", {
            message: errorMessage,
            request: "no request in error",
            response: "no response in error",
          });
        }

        event.addMetadata("debug", "traceId", getTraceId());
      },
    });

    Boundary =
      Bugsnag.getPlugin("react")?.createErrorBoundary(React) ?? React.Fragment;
  }
}

export { initialiseBugsnag, BugsnagBoundary, Bugsnag };
